import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type { IListAssignee, IListField } from '../../api/lists-contracts';
import { changedWhen, formatListValue } from '../../lists';
import { authorOf, currentRaw, rawOf, type IAskedConflict } from './editing';

/**
 * The collision dialog (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.6; LST-02.5): one cell a save found saved by someone else since it was read, asked about one
 * at a time. "**Elena Duarte** changed **Priscilla Hale**'s Quality rating to *5 - Average* at
 * 10:41, while you were editing. Yours: *10 - High Quality*." — **Keep mine** resends that cell at
 * the version it holds now; **Take theirs** draws theirs and sends nothing; **Cancel**, and the
 * dialog closed any other way, is *Take theirs* (AC3 — no value half-saved).
 *
 * Who saved is the account's email, "an account since removed" without one; when, the viewer's
 * clock, with the day where it is not today.
 */
export function ConflictDialog({
  asked,
  fieldOf,
  accounts,
  onKeepMine,
  onTakeTheirs
}: {
  asked: Nullable<IAskedConflict>;
  fieldOf: (code: string) => IListField | undefined;
  accounts: readonly IListAssignee[];
  onKeepMine: (asked: IAskedConflict) => void;
  onTakeTheirs: (asked: IAskedConflict) => void;
}) {
  // The last one asked stays drawn while the dialog closes, so its words do not blank mid-fade.
  const [shown, setShown] = React.useState(asked);
  React.useEffect(() => {
    if (asked !== null) setShown(asked);
  }, [asked]);
  if (shown === null) return null;

  const { conflict, draft } = shown;
  const field = fieldOf(conflict.field);
  const label = field?.label ?? conflict.field;
  const words = (raw: unknown) => {
    if (raw === null || raw === undefined) return '';
    const email =
      draft.kind === 'assignee' && typeof raw === 'string'
        ? accounts.find((account) => account.accountId === raw)?.email
        : undefined;
    return field ? formatListValue(field, raw, email) : String(raw);
  };
  const theirs = words(currentRaw(draft.kind, conflict.current));
  const mine = words(rawOf(draft.kind, conflict.mine));
  const at = conflict.current.setAt ? ` ${changedWhen(conflict.current.setAt)}` : '';

  return (
    <AlertDialog
      open={asked !== null}
      onOpenChange={(open) => {
        if (!open && asked !== null) onTakeTheirs(asked);
      }}
    >
      <AlertDialogContent data-conflict="">
        <AlertDialogHeader>
          <AlertDialogTitle>Someone else saved this cell</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="grid gap-2">
              <p>
                <strong>{authorOf(conflict.current)}</strong>{' '}
                {theirs === '' ? 'cleared' : 'changed'} <strong>{draft.name}</strong>’s {label}
                {theirs === '' ? '' : (
                  <>
                    {' '}
                    to <em>{theirs}</em>
                  </>
                )}
                {at}, while you were editing.
              </p>
              <p>
                Yours: {mine === '' ? 'cleared' : <em>{mine}</em>}.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button type="button" variant="outline" onClick={() => asked && onTakeTheirs(asked)}>
            Take theirs
          </Button>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              if (asked) onKeepMine(asked);
            }}
          >
            Keep mine
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
